import { useCallback, useEffect, useRef, useState } from 'react';

export interface CellKey {
    coachId: string;
    date: string;
}

const keyOf = (k: CellKey) => `${k.coachId}|${k.date}`;

/**
 * 그리드 드래그 범위 선택 훅.
 *
 * - mousedown: 드래그 시작, 해당 셀을 anchor로 기록
 * - mouseenter: 드래그 중이면 anchor ~ 현재 셀 사이 사각형 범위를 선택
 * - mouseup: 드래그 종료 → onCommit 콜백으로 최종 선택 셀 리스트 전달
 *
 * 강사 행 순서는 coachOrder 배열로 전달받아 행 인덱스 범위 계산에 사용.
 * 날짜 순서는 dateOrder 배열로 전달.
 */
export const useDragSelection = ({
    coachOrder,
    dateOrder,
    onCommit,
}: {
    coachOrder: string[];
    dateOrder: string[];
    onCommit: (cells: CellKey[]) => void;
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selection, setSelection] = useState<Set<string>>(new Set());
    const anchorRef = useRef<CellKey | null>(null);

    const computeRange = useCallback(
        (a: CellKey, b: CellKey): CellKey[] => {
            const rA = coachOrder.indexOf(a.coachId);
            const rB = coachOrder.indexOf(b.coachId);
            const cA = dateOrder.indexOf(a.date);
            const cB = dateOrder.indexOf(b.date);
            if (rA < 0 || rB < 0 || cA < 0 || cB < 0) return [];
            const rMin = Math.min(rA, rB);
            const rMax = Math.max(rA, rB);
            const cMin = Math.min(cA, cB);
            const cMax = Math.max(cA, cB);
            const cells: CellKey[] = [];
            for (let r = rMin; r <= rMax; r++) {
                for (let c = cMin; c <= cMax; c++) {
                    cells.push({ coachId: coachOrder[r], date: dateOrder[c] });
                }
            }
            return cells;
        },
        [coachOrder, dateOrder],
    );

    const handleMouseDown = useCallback((cell: CellKey) => {
        anchorRef.current = cell;
        setIsDragging(true);
        setSelection(new Set([keyOf(cell)]));
    }, []);

    const handleMouseEnter = useCallback(
        (cell: CellKey) => {
            if (!isDragging || !anchorRef.current) return;
            const range = computeRange(anchorRef.current, cell);
            setSelection(new Set(range.map(keyOf)));
        },
        [isDragging, computeRange],
    );

    const commitIfDragging = useCallback(() => {
        if (!isDragging) return;
        const anchor = anchorRef.current;
        if (!anchor) {
            setIsDragging(false);
            setSelection(new Set());
            return;
        }
        const cells: CellKey[] = [];
        selection.forEach((k) => {
            const [coachId, date] = k.split('|');
            cells.push({ coachId, date });
        });
        setIsDragging(false);
        anchorRef.current = null;
        setSelection(new Set());
        if (cells.length > 0) onCommit(cells);
    }, [isDragging, selection, onCommit]);

    // 전역 mouseup으로 드래그 종료 처리 (그리드 바깥에서 놔도 반응)
    useEffect(() => {
        if (!isDragging) return;
        const handler = () => commitIfDragging();
        window.addEventListener('mouseup', handler);
        return () => window.removeEventListener('mouseup', handler);
    }, [isDragging, commitIfDragging]);

    const isSelected = useCallback((cell: CellKey) => selection.has(keyOf(cell)), [selection]);

    return {
        isDragging,
        isSelected,
        handleMouseDown,
        handleMouseEnter,
    };
};
